import React, { useState, useCallback } from 'react';
import { Authenticator, Sidebar, Input, Button } from 'components';
import { SidebarProvider } from 'context/SidebarContext';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import {
    Campaigns,
    ErrorScreen,
    NotFoundScreen,
    LoginScreen,
    NewLoginScreen,
    LogoutScreen,
    SwitchingVersionScreen,
    CartScreen,
    CodCheckout,
    DashboardScreen,
    FaqScreen,
    PrivacyPolicy,
    ManualSetup,
    PlanScreen,
    StickyCartSettings,
    InvitationScreen,
    Affiliation,
    ReferralScreen,
    Integrations
} from 'screens';
import { withCampaignProvider } from 'context/CampaignContext';
import { isEmbedded } from 'lib/wrappers';
import styles from 'styles/page.module.scss';
import useReactRouter from 'use-react-router';
import classNames from 'lib/classNames';
import { API } from 'aws-amplify';
import TutorialModal from 'components/TutorialModal';
import TallyModal from 'components/TallyModal';
import SetupScreen from 'screens/SetupScreen';
import MonsterCartV2Announcement from 'components/MonsterCartV2Announcement';

function App() {
    return (
        <BrowserRouter>
            <ErrorScreen>
                <Routes />
            </ErrorScreen>
        </BrowserRouter>
    );
}

export default App;

const Routes = () => {
    const [voucher, setVoucher] = useState('');
    const [newPlanRedirect, setNewPlanRedirect] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const { location } = useReactRouter();
    // const [collapsed, originalSetCollapsed] = useState(
    //   // JSON.parse(localStorage.getItem("sidebar_collapsed"))
    // );
    // const setCollapsed = (value) => {
    //   // localStorage.setItem("sidebar_collapsed", value);
    //   originalSetCollapsed(value);
    // };
    const [collapsed, setCollapsed] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [upgradeError, setUpgradeError] = useState(null);

    async function upgrade() {
        if (!window.shop) {
            setUpgradeError('Shop not found. Please reload the page.');
            return;
        }
        setIsUpgrading(true);
        setUpgradeError(null);

        try {
            const data = await fetch(`https://monster-worker.com/upgrade?shop=${window.shop}`, {
                method: 'POST',
                body: JSON.stringify({ shopName: window.shop }),
                headers: {
                    'Content-Type': 'application/json'
                }
            }).then((res) => res.json());
            if (data.success) {
                const params = new URLSearchParams(window.location.search);
                params.set('shop', window.shop);
                params.set('target', '2');
                window.location.href = `/switching-version?${params.toString()}`;
            } else {
                setIsUpgrading(false);
                setUpgradeError('Upgrade failed. Please try again.');
            }
        } catch (e) {
            setIsUpgrading(false);
            setUpgradeError('Unexpected error. Please try again.');
        }
    }

    if (
        ['/login', '/auth/shopify/callback'].includes(location.pathname) ||
        location.pathname.startsWith('/switching-version') ||
        new RegExp('/invitation/.*').test(location.pathname) ||
        new RegExp('/ref/.*').test(location.pathname)
    )
        return (
            <Switch>
                {/* <Route path="/login" component={LoginScreen} /> */}
                <Route path="/login" component={NewLoginScreen} />
                <Route path="/invitation/:id" component={InvitationScreen} />
                <Route path="/ref/:code" component={ReferralScreen} />
                {/* Shared limbo route mirroring v2's /switching-version.
                    Pre-auth so it doesn't require a Shopify session.
                    NOTE: with the monster-worker change forcing /switching-version
                    always to v2, this route on v1 is unreachable in practice —
                    kept as a defensive fallback. */}
                <Route path="/switching-version" component={SwitchingVersionScreen} />
            </Switch>
        );
    else
        return (
            <Authenticator>
                <>
                    <MonsterCartV2Announcement
                        onUpgrade={upgrade}
                        isUpgrading={isUpgrading}
                        upgradeError={upgradeError}
                    />
                    <div
                        className={classNames(
                            'hidden px-20 pr-8 mx-auto py-2 flex-center bg-indigo-800 text-white font-bold'
                        )}
                    >
                        <span className={classNames('flex-1')}>Have a voucher code?</span>
                        <div className={classNames('w-2/3 flex items-center justify-end')}>
                            <Input
                                value={voucher}
                                onChange={(value) => setVoucher(value)}
                                isFlex
                                placeholder="Voucher code"
                            />
                            <Button
                                text="Use It Now"
                                type="secondary2"
                                className={classNames('ml-3')}
                                onClick={async () => {
                                    const planResponse = await API.post(
                                        'API_GATEWAY_NEW',
                                        '/shop/get_plan',
                                        {
                                            body: {
                                                voucher
                                            }
                                        }
                                    );
                                    setVoucher('');
                                    setNewPlanRedirect(planResponse.confirmation_url);
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.app_wrapper} data-collapsed={collapsed}>
                        <SidebarProvider value={{ collapsed, setCollapsed }}>
                            <Sidebar />
                        </SidebarProvider>
                        <Switch>
                            <Route path="/logout" component={LogoutScreen} />
                            <Route
                                path="/"
                                component={isEmbedded({
                                    ChildComponent: SetupScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                                exact={true}
                            />
                            <Route
                                path="/campaigns"
                                component={isEmbedded({
                                    ChildComponent: Campaigns.list,
                                    previewOpenByDefault: false,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                                exact={true}
                            />
                            <Route
                                path="/campaigns/create"
                                component={isEmbedded({
                                    ChildComponent: withCampaignProvider(
                                        Campaigns.create,
                                        null,
                                        null,
                                        setIsOpen
                                    ),
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                                exact={true}
                            />
                            <Route
                                path="/campaigns/create/:id"
                                component={isEmbedded({
                                    ChildComponent: Campaigns.duplicate,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                            />
                            <Route
                                path="/campaigns/:id"
                                component={isEmbedded({
                                    ChildComponent: Campaigns.update,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                            />
                            <Route
                                path="/settings"
                                component={isEmbedded({
                                    ChildComponent: CartScreen,
                                    previewOpenByDefault: true,
                                    noCampaign: true,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />

                            <Route
                                path="/sticky"
                                component={isEmbedded({
                                    ChildComponent: StickyCartSettings,
                                    previewOpenByDefault: true,
                                    noCampaign: true,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/settings/checkout"
                                component={isEmbedded({
                                    ChildComponent: CodCheckout,
                                    previewOpenByDefault: true,
                                    noCampaign: true,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/settings/setup"
                                component={isEmbedded({
                                    ChildComponent: ManualSetup,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/dashboard"
                                component={isEmbedded({
                                    ChildComponent: DashboardScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/plan"
                                component={isEmbedded({
                                    ChildComponent: PlanScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />
                            <Route
                                path="/integrations"
                                component={isEmbedded({
                                    ChildComponent: Integrations,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />
                            <Route
                                path="/faq"
                                component={isEmbedded({
                                    ChildComponent: FaqScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />
                            <Route
                                path="/privacy_policy"
                                component={isEmbedded({
                                    ChildComponent: PrivacyPolicy,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />

                            <Route
                                path="/affiliation"
                                component={isEmbedded({
                                    ChildComponent: Affiliation,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route component={NotFoundScreen} />
                        </Switch>
                        <TutorialModal isOpen={isOpen} setIsOpen={setIsOpen} />
                        <TallyModal />
                    </div>
                </>
            </Authenticator>
        );
};
